<!-- ============================================================
  ExpiryRequestDialog.vue (<expiry-request-dialog>) — ขอเลื่อนวันหมดอายุคอร์สหลายใบพร้อมกัน
  ออกแบบ: C:\iStarApp\COURSE_EXPIRY_ADJUST_DESIGN.md

  • แอดมินยื่น → รอผู้จัดการอนุมัติ · ผู้จัดการยื่น → มีผลทันที (ปุ่มส่งเปลี่ยนคำตามบทบาท)
  • วันที่ใหม่คำนวณที่ backend (/previewExpiryAdjust) หน้าจอเป็นแค่ตัวแสดง — ไม่คำนวณเอง
  • ยิมปิดวันจันทร์: ถ้าช่วงที่ใบจะได้เพิ่มมีวันจันทร์ จะเตือนรายใบ + ถามว่าจะบวกวันชดเชยเพิ่มไหม
    (เจ้าของเคาะ 2026-10-18: ไม่มีสูตรตายตัว แอดมินเลือกเอง และมีปุ่ม "บวกให้ทุกใบ")
  • สลิปแนบรายใบ (สูงสุด 3) + ใบรับรองแพทย์ (สูงสุด 3 แยกช่อง ไม่ตรวจสลิป) — ไปรอในตารางพักของคำขอ เข้าใบคอร์สตอนผู้จัดการอนุมัติ
============================================================ -->
<template>
  <id-modal :model-value="modelValue" size="xl" icon="mdi-calendar-expand-horizontal"
    :title="editing ? $t('expiryReq.editDialogTitle', { id: editing.request.requestid }) : $t('expiryReq.dialogTitle')"
    persistent @update:model-value="v => $emit('update:modelValue', v)">
    <div class="modal-sec"><span class="mdi mdi-format-list-bulleted"></span> {{ $t('expiryReq.pickCourses') }}</div>
    <!-- 1 ช่อง = 1 รหัส: พิมพ์รหัสแล้วกด Enter (หรือปุ่มเพิ่ม) ระบบสร้างเป็นแถวของคอร์สนั้น แล้วช่องว่างรอรหัสถัดไป
         (วางหลายรหัสทีเดียวก็ได้ — แยกเป็นแถวให้เอง) -->
    <div class="field full">
      <label>{{ $t('expiryReq.codesLabel') }}</label>
      <div class="er-add">
        <input class="id-input" v-model="codesText" :placeholder="$t('expiryReq.codesPh')" autocomplete="off" name="expiry-code"
          @keydown.enter.prevent="e => { if (!e.isComposing && e.keyCode !== 229) addCodes(); }" @paste="onPaste">
        <button class="id-btn id-btn-primary id-btn-sm" :disabled="!codesText.trim() || busy" @click="addCodes">
          <span class="mdi mdi-plus"></span> {{ $t('expiryReq.addCodes') }}
        </button>
      </div>
    </div>
    <div class="er-actions">
      <span class="er-spacer"></span>
      <label class="er-days-all">
        {{ $t('expiryReq.daysAll') }}
        <input class="id-input er-days-input" type="number" min="1" max="366" v-model.number="globalDays" autocomplete="off" name="expiry-days-all">
      </label>
      <button class="id-btn id-btn-ghost id-btn-sm" :disabled="!rows.length || busy" @click="applyGlobalDays">{{ $t('expiryReq.applyAll') }}</button>
    </div>

    <template v-if="rows.length">
      <div v-if="mondayRows.length" class="er-bulk">
        <span class="mdi mdi-calendar-alert"></span>
        {{ $t('expiryReq.mondayBulk', { n: mondayRows.length }) }}
        <button class="id-btn id-btn-primary id-btn-sm" :disabled="busy" @click="addMondayAll">{{ $t('expiryReq.addMondayAll') }}</button>
      </div>

      <div class="er-rows">
        <div v-for="r in rows" :key="r.courserefer" class="er-row" :class="{ 'is-bad': !r.ok }">
          <div class="er-main">
            <div class="er-code">{{ r.courserefer }}
              <span v-if="r.ok" class="badge badge-primary">{{ r.coursetype === 'Monthly' ? $t('home.monthly') : r.coursetype }}</span>
            </div>
            <div v-if="r.ok" class="t-cap">{{ r.coursename }}</div>
            <div v-else class="er-err"><span class="mdi mdi-alert-circle-outline"></span> {{ r.message }}</div>
          </div>
          <template v-if="r.ok">
            <div class="er-dates">
              <span class="t-cap">{{ $t('expiryReq.expiryOld') }}</span>
              <span>{{ fmt(r.old) }}</span>
              <span class="mdi mdi-arrow-right"></span>
              <span class="t-cap">{{ $t('expiryReq.expiryNew') }}</span>
              <b>{{ fmt(r.new) }}</b>
              <span class="badge badge-success">+{{ r.days + r.extra }} {{ $t('expiryReq.days') }}</span>
            </div>
            <label class="er-days">
              {{ $t('expiryReq.days') }}
              <input class="id-input er-days-input" type="number" min="1" max="366" :value="r.days"
                autocomplete="off" :name="'expiry-days-' + r.courserefer" @change="e => onDays(r, e.target.value)">
            </label>
            <button class="er-del" v-hint="$t('expiryReq.removeRow')" :aria-label="$t('expiryReq.removeRow')" @click="removeRow(r)">
              <span class="mdi mdi-close"></span>
            </button>
            <!-- ข้อมูลคอร์สให้คนยื่นเห็นตั้งแต่ตอนกรอก: ประเภท · ระยะเวลา/จำนวนครั้ง · คงเหลือ · เริ่ม · ใครใช้ -->
            <div class="er-facts">
              <span v-if="r.coursetype === 'Monthly' && r.period"><span class="t-cap">{{ $t('customerCourse.period') }}</span> {{ r.period }} {{ $t('expiryReq.months') }}</span>
              <span v-else-if="r.coursetype !== 'Monthly'"><span class="t-cap">{{ $t('expiryReq.sessions') }}</span> {{ r.totalSessions }}</span>
              <span v-if="r.coursetype !== 'Monthly'"><span class="t-cap">{{ $t('table.remaining') }}</span> {{ r.remaining }}</span>
              <span><span class="t-cap">{{ $t('table.startDate') }}</span> {{ fmt(r.startdate) }}</span>
              <span><span class="t-cap">{{ $t('expiryReq.users') }}</span> {{ r.users && r.users.length ? r.users.join(', ') : '-' }}</span>
            </div>
            <!-- วันจันทร์ (ยิมปิด): แอดมินเลือกเองว่าจะบวกชดเชยกี่วัน 0..จำนวนจันทร์ในช่วงนั้น (สูงสุด = maxExtra) -->
            <div v-if="r.maxExtra > 0" class="er-monday" :class="{ 'is-ok': r.extra >= r.maxExtra }">
              <span class="mdi mdi-calendar-alert"></span>
              <span v-if="r.mondays.length">{{ $t('expiryReq.mondayPick', { n: r.mondays.length, dates: r.mondays.map(fmt).join(', '), max: r.maxExtra }) }}</span>
              <span v-else>{{ $t('expiryReq.extraAdded', { n: r.extra }) }} · {{ $t('expiryReq.extraMax', { max: r.maxExtra }) }}</span>
              <id-select class="er-extra-select" :model-value="r.extra" :disabled="busy"
                :options="extraOptions(r)" @update:model-value="v => setExtra(r, Number(v))"></id-select>
            </div>
            <div class="er-slips">
              <label class="id-btn id-btn-ghost id-btn-sm er-file">
                <span class="mdi mdi-paperclip"></span> {{ $t('expiryReq.attachSlip') }} ({{ slotsUsed(r) }}/3)
                <input type="file" accept="image/*" multiple @change="e => onFiles(r, e)">
              </label>
              <span v-for="s in r.existing" :key="'e' + s.slipid" class="er-chip" :class="{ 'is-removed': r.removeIds.includes(s.slipid) }">
                <img :src="s.slip_url" class="er-mini" alt="">
                {{ s.filename }}
                <button class="er-chip-x" :aria-label="$t('expiryReq.removeRow')" @click="toggleRemoveSlip(r, s.slipid)">
                  <span class="mdi" :class="r.removeIds.includes(s.slipid) ? 'mdi-undo' : 'mdi-close'"></span>
                </button>
              </span>
              <span v-for="(f, i) in r.files" :key="i" class="er-chip">
                {{ f.name }}
                <button class="er-chip-x" :aria-label="$t('expiryReq.removeRow')" @click="r.files.splice(i, 1)"><span class="mdi mdi-close"></span></button>
              </span>
            </div>
            <!-- ใบรับรองแพทย์: ช่องแนบแยกจากสลิป (ไม่ตรวจสลิป ไม่เข้าใบคอร์ส) เก็บกับคำขอให้ผู้จัดการดูประกอบการอนุมัติ -->
            <div class="er-slips">
              <label class="id-btn id-btn-ghost id-btn-sm er-file">
                <span class="mdi mdi-medical-bag"></span> {{ $t('expiryReq.attachMedical') }} ({{ medUsed(r) }}/3)
                <input type="file" accept="image/*" multiple @change="e => onMedFiles(r, e)">
              </label>
              <span v-for="s in r.medExisting" :key="'m' + s.slipid" class="er-chip" :class="{ 'is-removed': r.removeIds.includes(s.slipid) }">
                <img :src="s.slip_url" class="er-mini" alt="">
                {{ s.filename }}
                <button class="er-chip-x" :aria-label="$t('expiryReq.removeRow')" @click="toggleRemoveSlip(r, s.slipid)">
                  <span class="mdi" :class="r.removeIds.includes(s.slipid) ? 'mdi-undo' : 'mdi-close'"></span>
                </button>
              </span>
              <span v-for="(f, i) in r.medFiles" :key="'mf' + i" class="er-chip">
                {{ f.name }}
                <button class="er-chip-x" :aria-label="$t('expiryReq.removeRow')" @click="r.medFiles.splice(i, 1)"><span class="mdi mdi-close"></span></button>
              </span>
            </div>
          </template>
          <!-- ใบที่ขอเลื่อนไม่ได้ต้องเอาออกได้ ไม่งั้นปุ่มส่งถูกล็อกถาวร -->
          <button v-else class="er-del" v-hint="$t('expiryReq.removeRow')" :aria-label="$t('expiryReq.removeRow')" @click="removeRow(r)">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
      </div>

      <div class="modal-sec mt"><span class="mdi mdi-text-box-edit-outline"></span> {{ $t('expiryReq.reasonTitle') }}</div>
      <div class="field full">
        <label>{{ $t('expiryReq.reason') }} <span class="req">*</span></label>
        <input class="id-input" v-model="reason" maxlength="500" :placeholder="$t('expiryReq.reasonPh')" autocomplete="off" name="expiry-reason">
        <div class="t-cap" style="margin-top:4px">{{ editing ? $t('expiryReq.hintEdit') : (isHead ? $t('expiryReq.hintHead') : $t('expiryReq.hintAdmin')) }}</div>
      </div>
    </template>

    <template #footer>
      <button class="id-btn id-btn-ghost" :disabled="submitting" @click="close">{{ $t('btn.cancel') }}</button>
      <button class="id-btn id-btn-primary" :disabled="!canSubmit" @click="submit">
        <span class="mdi mdi-send"></span> {{ editing ? $t('expiryReq.saveEdit') : (isHead ? $t('expiryReq.submitHead') : $t('expiryReq.submitAdmin')) }}
      </button>
    </template>
  </id-modal>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ExpiryRequestDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    isHead: { type: Boolean, default: false },
    // แก้ไขคำขอที่ยื่นไว้: ส่ง detail ของคำขอ (จาก /getExpiryRequestDetail) เข้ามา · ไม่ส่ง = ยื่นใหม่
    editing: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'done', 'onLoading', 'onErrorHandler'],
  data: () => ({
    codesText: '', globalDays: 2, rows: [], reason: '', removedSlipIds: [], inflight: 0, session: 0, submitting: false,
  }),
  computed: {
    token() { return this.$store.getters.getToken; },
    busy() { return this.inflight > 0; },
    mondayRows() { return this.rows.filter((r) => r.ok && r.maxExtra > 0 && r.extra < r.maxExtra); },
    canSubmit() {
      // loaded: แถวต้องได้ผลตรวจจาก backend แล้วทุกแถว — ไม่งั้นแถวที่ preview ล้ม/ยังไม่มาจะหลุดไปโดยไม่เห็นคำเตือนวันจันทร์
      return !this.submitting && !this.busy && this.rows.length > 0 && this.rows.every((r) => r.ok && r.loaded)
        && this.reason.trim().length > 0;
    },
  },
  watch: {
    modelValue(open) { if (open) { this.reset(); if (this.editing) this.loadEditing(); } },
  },
  methods: {
    reset() { this.session += 1; this.removedSlipIds = []; this.codesText = ''; this.rows = []; this.reason = ''; this.globalDays = 2; this.inflight = 0; this.submitting = false; },
    close() { this.$emit('update:modelValue', false); },
    fmt(d) { return d ? this.$formatShortDate(d) : '-'; },
    headers() { return { headers: { Authorization: 'Bearer ' + this.token } }; },
    async preview(list) {
      const res = await axios.post(this.baseURL + '/previewExpiryAdjust',
        { items: list.map((r) => ({ courserefer: r.courserefer, days: r.days, extra: r.extra || 0 })) }, this.headers());
      if (!res.data.success) throw new Error(res.data.message || 'preview failed');
      return res.data.items;
    },
    merge(row, p) {
      if (!p.ok) { Object.assign(row, { ok: false, loaded: true, message: p.message, mondays: [], extra: 0 }); return; }
      Object.assign(row, { ok: true, loaded: true, message: '', coursename: p.coursename, coursetype: p.coursetype, old: p.old, new: p.new,
        days: p.days, extra: p.extra, mondays: p.mondays, maxExtra: p.maxExtra ?? p.suggestedExtra, // fallback เผื่อ FE ขึ้นก่อน BE
        period: p.period, startdate: p.startdate, remaining: p.remaining, totalSessions: p.totalSessions, users: p.users || [] });
    },
    async refresh(list) {
      const session = this.session;
      const rows = list.map((r) => this.rows.find((x) => x.courserefer === r.courserefer)).filter(Boolean);
      const seqs = new Map(rows.map((r) => { r._seq = (r._seq || 0) + 1; return [r.courserefer, r._seq]; }));
      this.inflight += 1;
      try {
        const out = await this.preview(rows);
        if (session !== this.session) return; // dialog was reset/reopened while the request was in flight
        out.forEach((p) => {
          const row = this.rows.find((r) => r.courserefer === p.courserefer);
          if (row && row._seq === seqs.get(p.courserefer)) this.merge(row, p); // a newer edit on this row wins
        });
      } catch (e) {
        if (session === this.session) {
          rows.forEach((r) => { if (!r.loaded) Object.assign(r, { ok: false, loaded: true, message: this.$t('expiryReq.previewFailed'), mondays: [], extra: 0 }); });
          this.$emit('onErrorHandler', (e.response && e.response.data && e.response.data.message) || e.message);
        }
      } finally { if (session === this.session) this.inflight -= 1; }
    },
    // วางรหัสหลายตัว (คอลัมน์จากชีต/แชต): เบราว์เซอร์บางตัวตัดขึ้นบรรทัดใหม่ทิ้งในช่องบรรทัดเดียวทำให้รหัสติดกัน
    // จึงอ่านจากคลิปบอร์ดเองแล้วแยกด้วยช่องว่างก่อน
    onPaste(e) {
      const text = e.clipboardData && e.clipboardData.getData('text');
      if (!text) return;
      e.preventDefault();
      this.codesText = `${this.codesText} ${text}`.replace(/\s+/g, ' ').trim();
      this.addCodes();
    },
    async addCodes() {
      const all = [...new Set(this.codesText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
      const valid = (c) => /^[A-Za-z0-9ก-๙_-]{4,64}$/.test(c);
      const bad = all.filter((c) => !valid(c));
      const fresh = all.filter((c) => valid(c) && !this.rows.some((r) => r.courserefer === c)).slice(0, Math.max(0, 100 - this.rows.length));
      const days = Number(this.globalDays) || 2;
      const added = fresh.map((c) => ({ courserefer: c, ok: true, loaded: false, message: '', days, extra: 0, mondays: [], maxExtra: 0, files: [], existing: [], medFiles: [], medExisting: [], removeIds: [] }));
      this.rows.push(...added);
      this.codesText = bad.join(' '); // รหัสที่อ่านไม่ได้ค้างไว้ให้เห็น ไม่หายเงียบ ๆ
      if (bad.length) this.$emit('onErrorHandler', this.$t('expiryReq.badCodes', { codes: bad.join(', ') }));
      if (added.length) await this.refresh(added);
    },
    async applyGlobalDays() {
      const days = Number(this.globalDays);
      if (!Number.isInteger(days) || days < 1 || days > 366) return;
      this.rows.forEach((r) => { r.days = days; r.extra = 0; });
      await this.refresh(this.rows);
    },
    async onDays(row, value) {
      const days = Number(value);
      if (!Number.isInteger(days) || days < 1 || days > 366) { this.$forceUpdate(); return; }
      row.days = days; row.extra = 0;
      await this.refresh([row]);
    },
    extraOptions(r) {
      return Array.from({ length: r.maxExtra + 1 }, (_, i) => ({ value: i, label: i === 0 ? this.$t('expiryReq.extraNone') : this.$t('expiryReq.extraOption', { n: i }) }));
    },
    async setExtra(row, n) { if (n === row.extra) return; row.extra = n; await this.refresh([row]); },
    async addMondayAll() {
      const todo = this.mondayRows;
      todo.forEach((r) => { r.extra = r.maxExtra; });
      await this.refresh(todo);
    },
    // เอาแถวออก: สลิปที่พักไว้ของคอร์สนั้นต้องถูกลบด้วยแม้พิมพ์รหัสเดิมกลับมาใหม่ภายหลัง (ไม่งั้นค้างซ่อนอยู่กินโควตา 3 ใบ)
    removeRow(row) { this.removedSlipIds.push(...(row.existing || []).concat(row.medExisting || []).map((x) => x.slipid)); this.rows = this.rows.filter((r) => r !== row); },
    // เปิดโหมดแก้ไข: ใส่ค่าเดิมของคำขอ (จำนวนวัน/วันชดเชยจันทร์/เหตุผล/สลิปที่พักไว้) แล้วให้ backend คำนวณวันใหม่จากวันหมดอายุปัจจุบัน
    async loadEditing() {
      const d = this.editing;
      this.reason = d.request.reason || '';
      this.rows = d.items.map((it) => ({
        courserefer: it.courserefer, ok: true, loaded: false, message: '', days: it.days_added - it.extra_days, extra: it.extra_days,
        mondays: [], maxExtra: 0, files: [], medFiles: [], removeIds: [],
        existing: (it.slips || []).filter((s) => s.kind !== 'medical').map((s) => ({ slipid: s.slipid, slip_url: s.slip_url, filename: s.filename })),
        medExisting: (it.slips || []).filter((s) => s.kind === 'medical').map((s) => ({ slipid: s.slipid, slip_url: s.slip_url, filename: s.filename })),
      }));
      await this.refresh(this.rows);
    },
    slotsUsed(r) { return r.files.length + (r.existing || []).filter((s) => !r.removeIds.includes(s.slipid)).length; },
    medUsed(r) { return r.medFiles.length + (r.medExisting || []).filter((s) => !r.removeIds.includes(s.slipid)).length; },
    onMedFiles(row, e) {
      const picked = Array.from(e.target.files || []);
      e.target.value = '';
      for (const f of picked) {
        if (this.medUsed(row) >= 3) { this.$emit('onErrorHandler', this.$t('expiryReq.medicalMax')); break; }
        row.medFiles.push(f);
      }
    },
    toggleRemoveSlip(r, id) { r.removeIds = r.removeIds.includes(id) ? r.removeIds.filter((x) => x !== id) : [...r.removeIds, id]; },
    onFiles(row, e) {
      const picked = Array.from(e.target.files || []);
      e.target.value = '';
      for (const f of picked) {
        if (this.slotsUsed(row) >= 3) { this.$emit('onErrorHandler', this.$t('expiryReq.slipMax')); break; }
        row.files.push(f);
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.$emit('onLoading', true);
      try {
        const fd = new FormData();
        const body = {
          reason: this.reason.trim(),
          items: this.rows.map((r) => ({ courserefer: r.courserefer, days: r.days, extra: r.extra })),
        };
        if (this.editing) {
          body.requestid = this.editing.request.requestid;
          body.removeSlipIds = [...new Set([...this.removedSlipIds, ...this.rows.flatMap((r) => r.removeIds)])];
        }
        fd.append('payload', JSON.stringify(body));
        this.rows.forEach((r) => {
          r.files.forEach((f) => fd.append('slip__' + r.courserefer, f));
          r.medFiles.forEach((f) => fd.append('med__' + r.courserefer, f));
        });
        const res = await axios.post(this.baseURL + (this.editing ? '/updateExpiryRequest' : '/createExpiryRequest'), fd, this.headers());
        if (!res.data.success) { this.$emit('onErrorHandler', res.data.message); return; }
        this.$emit('done', { ...res.data, updated: !!this.editing });
        this.close();
      } catch (e) {
        this.$emit('onErrorHandler', (e.response && e.response.data && e.response.data.message) || e.message);
      } finally {
        this.submitting = false;
        this.$emit('onLoading', false);
      }
    },
  },
};
</script>

<style scoped>
.er-add { display: flex; gap: 8px; align-items: center; }
.er-add .id-input { flex: 0 1 260px; height: 34px; font-size: 13px; padding: 0 12px; } /* ช่องรหัสไม่ต้องยาวเต็มแถว — รหัสคอร์สสั้น */
.er-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
.er-spacer { flex: 1; }
.er-days-all { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; }
.er-days-input { width: 76px; height: 34px; text-align: center; }
.er-bulk { margin-top: 14px; padding: 10px 12px; border-radius: 10px; background: var(--c-warning-bg); color: var(--c-warning-tx);
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-weight: 600; }
.er-rows { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
.er-row { position: relative; display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.6fr) auto auto; gap: 8px 14px; align-items: center;
  padding: 12px; border: 1px solid var(--c-border, #eee); border-radius: 12px; background: var(--c-surface, #fff); }
.er-row.is-bad { border-color: var(--c-error); }
.er-code { font-weight: 700; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.er-err { color: var(--c-error-tx); font-size: 13px; }
.er-dates { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.er-days { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.er-del { border: none; background: transparent; cursor: pointer; font-size: 18px; color: var(--c-error); }
.er-monday { grid-column: 1 / -1; padding: 8px 10px; border-radius: 8px; background: var(--c-warning-bg); color: var(--c-warning-tx);
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; }
.er-facts { grid-column: 1 / -1; display: flex; gap: 6px 18px; flex-wrap: wrap; font-size: 12.5px; }
.er-facts .t-cap { margin-right: 4px; }
.er-monday .er-extra-select { flex: 0 0 auto; width: 150px; height: 34px; font-size: 13px; margin-left: auto; }
.er-monday > span:not(.mdi) { flex: 1 1 240px; }
.er-monday.is-ok { background: var(--c-success-bg); color: var(--c-success-tx); }
.er-slips { grid-column: 1 / -1; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.er-file { position: relative; overflow: hidden; cursor: pointer; }
.er-file input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.er-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px; background: var(--c-primary-soft); font-size: 12px; }
.er-chip.is-removed { opacity: .5; text-decoration: line-through; }
.er-mini { width: 22px; height: 22px; object-fit: cover; border-radius: 4px; }
.er-chip-x { border: none; background: transparent; cursor: pointer; padding: 0; color: inherit; display: inline-flex; }
@media (max-width: 760px) { .er-row { grid-template-columns: 1fr auto; } }
</style>
